import { pipe } from 'fp-ts/function';
import { record } from 'fp-ts';
import { CheckInConfigurationType, CheckInConfigurationValue } from '@mews-commander/data-access';
import {
    CheckboxValues,
    CheckInCheckboxColumnName,
    CheckInCheckboxStates,
    CheckInColumnName,
    CheckInFieldsValues,
    DocumentTypeField,
} from '../types';
import { calculateDocumentVisibilityChange, changeDocumentType } from '../documentsTable/documentUtils';
import { TabItemValue } from '../CheckInTabs/enums';

export interface CheckInReducerState {
    selectedDocumentType: DocumentTypeField | null;
    values: CheckInFieldsValues;
    checkboxesState: CheckInCheckboxStates;
}

export interface ChangeDocumentVisibilityAction {
    type: 'CHANGE_DOCUMENT_VISIBILITY';
    payload: {
        visibility: CheckInConfigurationValue;
        enhancedCheckInFormDocumentsSectionEnabled: boolean;
    };
}

export interface ChangeDocumentTypeAction {
    type: 'CHANGE_DOCUMENT_TYPE';
    payload: {
        documentType: DocumentTypeField | null;
    };
}

interface ChangeVisibilityDataAction {
    type: 'CHANGE_VISIBILITY_DATA';
    payload: {
        type: CheckInConfigurationType;
        value: CheckInConfigurationValue;
        columnName: CheckInColumnName;
    };
}

interface ChangeCheckboxState {
    type: 'CHANGE_CHECKBOX_STATE';
    payload: {
        tabValue: TabItemValue;
        checkboxValues: CheckboxValues;
    };
}
interface SetCheckboxState {
    type: 'SET_CHECKBOX_STATE';
    payload: {
        checkboxesState: CheckInCheckboxStates;
        changedCheckboxColumn: CheckInCheckboxColumnName;
    };
}

type ReducerAction =
    | ChangeDocumentVisibilityAction
    | ChangeDocumentTypeAction
    | ChangeVisibilityDataAction
    | ChangeCheckboxState
    | SetCheckboxState;

export const initialState: CheckInReducerState = {
    selectedDocumentType: null,
    values: {},
    checkboxesState: {},
};

export const checkInFieldsReducer = (state: CheckInReducerState, action: ReducerAction): CheckInReducerState => {
    switch (action.type) {
        case 'CHANGE_DOCUMENT_TYPE': {
            const { documentType } = action.payload;
            return {
                ...state,
                selectedDocumentType: documentType,
                values: changeDocumentType(documentType, state.values),
            };
        }
        case 'CHANGE_DOCUMENT_VISIBILITY': {
            const { visibility, enhancedCheckInFormDocumentsSectionEnabled } = action.payload;
            const { selectedDocumentType, values: oldCheckInFieldsValues } = state;

            if (!selectedDocumentType) {
                return state;
            }

            const values = calculateDocumentVisibilityChange({
                selectedDocumentType,
                oldCheckInFieldsValues,
                visibility,
                enhancedCheckInFormDocumentsSectionEnabled,
            });

            return {
                ...state,
                values,
            };
        }
        case 'SET_CHECKBOX_STATE': {
            const { checkboxesState: prevCheckboxesState, changedCheckboxColumn } = action.payload;

            const checkboxesState = pipe(
                prevCheckboxesState,
                record.mapWithIndex((sectionKey, checkboxSectionValues) => ({
                    ...state.checkboxesState[sectionKey],
                    [changedCheckboxColumn]: checkboxSectionValues[changedCheckboxColumn],
                }))
            );

            return {
                ...state,
                checkboxesState,
            };
        }
        case 'CHANGE_CHECKBOX_STATE': {
            const { tabValue, checkboxValues } = action.payload;
            return {
                ...state,
                checkboxesState: {
                    ...state.checkboxesState,
                    [tabValue]: checkboxValues,
                },
            };
        }
        case 'CHANGE_VISIBILITY_DATA':
            return {
                ...state,
                values: {
                    ...state.values,
                    [action.payload.type]: {
                        ...state.values[action.payload.type],
                        [action.payload.columnName]: action.payload.value,
                    },
                },
            };
        default:
            return state;
    }
};
